<div class="accounts-table-container">
  <div class="table-header">
    <h2 class="section-title">Accounts</h2>
  </div>

  <p-table
    [scrollable]="true"
    [value]="filteredAccounts()"
    [loading]="isLoading()"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [tableStyle]="{ 'min-width': '50rem' }"
  >
    <ng-template pTemplate="header">
      <tr>
        @for (col of columns(); track col.field) {
          <th [pSortableColumn]="col.sortable ? col.field : undefined">
            {{ col.header }}
            @if (col.sortable) {
              <p-sortIcon></p-sortIcon>
            }
          </th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-account>
      <tr>
        <td>
          <strong>{{ account.name }}</strong>
        </td>
        <td>
          <div>{{ account.userName }}</div>
          @if (account.userEmail) {
            <small class="secondary-text">{{ account.userEmail }}</small>
          }
        </td>
        <td>
          <span class="secondary-text">{{ account.description || 'No description' }}</span>
        </td>
        <td>
          @if (account.groups.length > 0) {
            <p-tag
              severity="info"
              [value]="account.groups.length + ' group' + (account.groups.length !== 1 ? 's' : '')"
            ></p-tag>
          } @else {
            <span class="secondary-text">No groups</span>
          }
        </td>
        <td>
          <p-tag [value]="getStatusLabel(account.isActive)" [severity]="getStatusSeverity(account.isActive)"></p-tag>
        </td>
        <td>{{ formatDate(account.createdOn) }}</td>
        <td>
          <div class="action-buttons">
            <p-button
              icon="pi pi-box"
              severity="success"
              pTooltip="Manage Packages"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              (onClick)="onManagePackages(account)"
            ></p-button>
            <p-button
              icon="pi pi-trash"
              severity="danger"
              pTooltip="Delete"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [loading]="deletingId() === account.accountId"
              (onClick)="onDeleteAccount(account)"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length">
          <div class="empty-state">
            <i class="pi pi-users large-icon"></i>
            <p>No accounts found</p>
            @if (searchTerm()) {
              <small>Try adjusting your search criteria</small>
            } @else {
              <small>Create your first account to get started</small>
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-dialog
  header="Create Account"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showDialog"
>
  <div class="dialog-content">
    <div class="form-field">
      <label for="userId">Owner User *</label>
      <p-select
        class="field-input"
        id="userId"
        optionLabel="name"
        optionValue="userId"
        placeholder="Select user"
        appendTo="body"
        [options]="users()"
        [filter]="true"
        [(ngModel)]="accountForm.userId().value"
      >
        <ng-template pTemplate="selectedItem" let-selectedItem>
          @if (selectedItem) {
            <div>{{ selectedItem.name }}</div>
            <small class="secondary-text">{{ selectedItem.email }}</small>
          }
        </ng-template>
        <ng-template pTemplate="item" let-user>
          <div>{{ user.name }}</div>
          <small class="secondary-text">{{ user.email }}</small>
        </ng-template>
      </p-select>
      @let userIdField = accountForm.userId();
      @if (userIdField.touched() && !userIdField.valid()) {
        <small class="p-error">
          @for (error of userIdField.errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="name">Account Name *</label>
      <input
        class="field-input"
        id="name"
        type="text"
        pInputText
        placeholder="Enter account name"
        [formField]="accountForm.name"
      />
      @let nameField = accountForm.name();
      @if (nameField.touched() && !nameField.valid()) {
        <small class="p-error">
          @for (error of nameField.errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="description">Description</label>
      <textarea
        class="field-input"
        id="description"
        placeholder="Enter account description"
        rows="3"
        [formField]="accountForm.description"
      ></textarea>
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="accountForm.isActive" />
        <span>Active (account is enabled)</span>
      </label>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="showDialog.set(false)"></p-button>
    <p-button label="Create" [loading]="saving()" [disabled]="!isFormValid()" (onClick)="onSaveAccount()"></p-button>
  </ng-template>
</p-dialog>

<!-- Manage Memberships Dialog -->
<p-dialog
  [header]="'Manage Packages - ' + (selectedAccountForMemberships()?.name || '')"
  [modal]="true"
  [style]="{ width: '50rem' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showMembershipsDialog"
>
  <div class="memberships-content">
    @if (membershipsLoading()) {
      <div class="loading-spinner">
        <p-progressSpinner></p-progressSpinner>
      </div>
    } @else {
      @if (accountMembershipSummary(); as summary) {
        <div class="memberships-list">
          <h3>User Memberships</h3>
          @if (summary.memberships && summary.memberships.length > 0) {
            @for (userSummary of summary.memberships; track userSummary.userId) {
              <div class="user-memberships">
                <h4>
                  {{ userSummary.name }}
                  <span class="user-email">({{ userSummary.email }})</span>
                </h4>
                <div class="memberships-count">
                  <p-tag severity="success" [value]="userSummary.activeMembershipCount + ' active'"></p-tag>
                  @if (userSummary.memberships.length > userSummary.activeMembershipCount) {
                    <p-tag
                      severity="secondary"
                      [value]="userSummary.memberships.length - userSummary.activeMembershipCount + ' cancelled'"
                    ></p-tag>
                  }
                </div>

                @if (userSummary.memberships && userSummary.memberships.length > 0) {
                  <div class="membership-items">
                    @for (membership of userSummary.memberships; track membership.membershipId) {
                      <div class="membership-item">
                        <div class="membership-details">
                          <strong>{{ membership.packageName }}</strong>
                          <span class="option-name">{{ membership.optionName }}</span>
                          <div class="membership-dates">
                            <small>Assigned: {{ formatDate(membership.assignedOn) }}</small>
                            @if (membership.cancelledOn) {
                              <small class="cancelled-date">Cancelled: {{ formatDate(membership.cancelledOn) }}</small>
                            }
                          </div>
                        </div>
                        <p-tag
                          [value]="membership.cancelledOn ? 'Cancelled' : 'Active'"
                          [severity]="membership.cancelledOn ? 'secondary' : 'success'"
                        ></p-tag>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          } @else {
            <div class="empty-state">
              <i class="pi pi-inbox medium-icon"></i>
              <p>No packages allocated yet</p>
            </div>
          }
        </div>
      }
    }
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Close" severity="secondary" (onClick)="showMembershipsDialog.set(false)"></p-button>
  </ng-template>
</p-dialog>
